import React, { useState } from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import {
    Calendar, Clock, RotateCcw, Save, ChevronRight, AlertCircle
} from 'lucide-react';
import '../styles/DataEntry.css'; // Import the new CSS file
import { dataEntrySchema, validateChlorineConstraint } from '../schemas/dataEntrySchema';

/**
 * DataEntry Component
 * Handles the collection and validation of hourly water quality metrics.
 * Submits data payload to the backend for BigQuery storage.
 */

export default function DataEntry() {
    const [submitStatus, setSubmitStatus] = useState(null);
    const backendUri = process.env.REACT_APP_BACKEND_URL || process.env.REACT_APP_BACKEND_URI || 'http://localhost:8080';

    const initialValues = {
        date: new Date().toISOString().split('T')[0],
        time: '14:00',
        raw_intake: '',
        pump_status: 'running',
        turbidity: '',
        ph: '',
        conductivity: '',
        pre_lime_dosage: '',
        pre_lime_flow: '',
        alum_dosage: '',
        alum_strength: '',
        alum_aerator_flow: '',
        alum_d_chamber: '',
        post_lime_sph1_dosage: '',
        post_lime_sph1_flow: '',
        post_lime_sph2_dosage: '',
        post_lime_sph2_flow: '',
        pre_chlorine_dosage: '',
        pre_chlorine_usage: '',
        post_chlorine_dosage: '',
        post_chlorine_usage: '',
        chlorine_tank_level: '',
        settled_pul_tube_turb: '',
        settled_pul_tube_ph: '',
        settled_pulsator_turb: '',
        settled_pulsator_ph: '',
        settled_cf1_turb: '',
        settled_cf1_ph: '',
        settled_cf2_turb: '',
        settled_cf2_ph: '',
        settled_pt1_turb: '',
        settled_pt1_ph: '',
        settled_pt2_turb: '',
        settled_pt2_ph: '',
        filtered_turbidity: '',
        filtered_ph: '',
        final_turbidity: '',
        final_ph: '',
        residual_chlorine: '',
        operator_name: '',
        notes: '',
        confirm_accuracy: false,
    };

    const handleSubmit = async (values, { setSubmitting }) => {
        try {
            // Validate cross-field constraint
            const chlorineErrors = validateChlorineConstraint(values);
            if (Object.keys(chlorineErrors).length > 0) {
                setSubmitStatus({ 
                    type: 'error', 
                    message: Object.values(chlorineErrors).join(', ') 
                });
                setSubmitting(false);
                return;
            }

            const endpoint = backendUri ? `${backendUri}/data` : '/data';
            const response = await fetch(endpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(values),
            });

            if (!response.ok) {
                const errorPayload = await response.json().catch(() => null);
                throw new Error(errorPayload?.error || `Failed to save record (${response.status})`);
            }

            setSubmitStatus({ 
                type: 'success', 
                message: 'Data entry saved to BigQuery successfully!' 
            });
            setSubmitting(false);

            // Clear success message after 3 seconds
            setTimeout(() => setSubmitStatus(null), 3000);
        } catch (error) {  
            setSubmitStatus({ 
                type: 'error', 
                message: error?.message || 'Error submitting data. Please try again.' 
            });
            setSubmitting(false);
        }
    };

    const ErrorDisplay = ({ name }) => (
        <ErrorMessage name={name}>
            {msg => (
                <div className="error-message">
                    <AlertCircle className="w-4 h-4" />
                    {msg}
                </div>
            )}
        </ErrorMessage>
    );

    return (
        <Formik
            initialValues={initialValues}
            validationSchema={dataEntrySchema}   
            onSubmit={handleSubmit}
            validateOnChange={true}   
            validateOnBlur={true}
        >
            {({ submitCount, isValid }) => (
            <div className="entry-page">
                <div className="entry-container">

                    {/* --- 1. Header --- */}
                    <header className="page-header">
                        <div>
                            <h1 className="page-title">Data Entry – Hourly Records</h1>
                            <nav>
                                <ol className="breadcrumbs">
                                    <li><a href="/" className="breadcrumb-link">Home</a></li>
                                    <ChevronRight className="w-4 h-4" />
                                    <li><span className="breadcrumb-active">Data Entry</span></li>
                                </ol>
                            </nav>
                        </div>

                        <div className="header-actions">
                            <div className="datetime-group">
                                <Calendar className="w-5 h-5 text-[#015486]" />
                                <Field
                                    type="date"
                                    name="date"
                                    className="datetime-input"
                                />
                                <Clock className="w-5 h-5 text-[#015486] ml-2" />
                                <Field
                                    type="time"
                                    name="time"
                                    className="datetime-input"
                                />
                            </div>
                            <button type="reset" form="data-entry-form" className="btn btn-secondary">
                                <RotateCcw className="w-4 h-4" /> Reset
                            </button>
                            <button type="submit" form="data-entry-form" className="btn btn-primary">
                                <Save className="w-4 h-4" /> Submit Record
                            </button>
                        </div>
                    </header>

                    {/* --- Status Messages --- */}
                    {submitStatus && (
                        <div className={`status-banner ${submitStatus.type}`}>
                            <AlertCircle className="w-5 h-5" />
                            {submitStatus.message}
                        </div>
                    )}

                    {submitCount > 0 && !isValid && (
                        <div className="status-banner error">
                            <AlertCircle className="w-5 h-5" />
                            Submission blocked by validation. Please complete all required fields and tick "I confirm all data entered is accurate".
                        </div>
                    )}

                    {/* --- Main Form --- */}
                    <Form id="data-entry-form" className="main-form">

                    {/* Section 1 & 2: Raw Water */}
                    <div className="grid-layout-3">
                        {/* Intake */}
                        <section className="section-card col-span-1">
                            <h2 className="section-title">Raw Water Intake</h2>
                            <div className="form-group">
                                <label className="form-label">Raw Water Intake <span className="required-mark">*</span></label>
                                <div className="input-wrapper">
                                    <Field
                                        type="number"
                                        name="raw_intake"
                                        className="form-input has-unit"
                                        placeholder="e.g., 1200"
                                        min="0"
                                        step="0.01"
                                    />
                                    <span className="unit-badge">m³/h</span>
                                </div>
                                <ErrorDisplay name="raw_intake" />
                            </div>
                            <div className="form-group">
                                <label className="form-label">Pump Status</label>
                                <Field
                                    as="select"
                                    name="pump_status"
                                    className="form-select"
                                >
                                    <option value="running">Running</option>
                                    <option value="standby">Standby</option>
                                    <option value="off">Off</option>
                                </Field>
                            </div>
                        </section>

                        {/* Quality */}
                        <section className="section-card col-span-2">
                            <h2 className="section-title">Raw Water Quality <span className="required-mark">*</span></h2>
                            <div className="inline-grid-3">
                                <div className="form-group">
                                    <label className="form-label">Turbidity</label>
                                    <div className="input-wrapper">
                                        <Field
                                            type="number"
                                            name="turbidity"
                                            className="form-input has-unit"
                                            placeholder="e.g., 5.2"
                                            min="0"
                                            step="0.1"
                                        />
                                        <span className="unit-badge">NTU</span>
                                    </div>
                                    <ErrorDisplay name="turbidity" />
                                </div>
                                <div className="form-group">
                                    <label className="form-label">pH</label>
                                    <Field
                                        type="number"
                                        name="ph"
                                        className="form-input"
                                        placeholder="e.g., 7.1"
                                        min="0"
                                        max="14"
                                        step="0.1"
                                    />
                                    <ErrorDisplay name="ph" />
                                </div>
                                <div className="form-group">
                                    <label className="form-label">Conductivity</label>
                                    <div className="input-wrapper">
                                        <Field
                                            type="number"
                                            name="conductivity"
                                            className="form-input has-unit"
                                            placeholder="e.g., 302"
                                            min="0"
                                            step="0.1"
                                        />
                                        <span className="unit-badge">µS/cm</span>
                                    </div>
                                    <ErrorDisplay name="conductivity" />
                                </div>
                            </div>
                        </section>
                    </div>

                    {/* Section 3: Chemical Dosing */}
                    <section className="section-card">
                        <h2 className="section-title">Chemical Dosing</h2>
                        <div className="grid-layout-3">
                            {/* Pre-Lime */}
                            <fieldset className="nested-fieldset">
                                <legend className="fieldset-legend">Pre-Lime Dosing</legend>
                                <div className="form-group">
                                    <label className="form-label text-gray">Dosage (ppm)</label>
                                    <Field type="number" name="pre_lime_dosage" className="form-input" placeholder="10.5" />
                                </div>
                                <div className="form-group">
                                    <label className="form-label text-gray">Flow Rate (L/min)</label>
                                    <Field type="number" name="pre_lime_flow" className="form-input" placeholder="20" />
                                </div>
                            </fieldset>

                            {/* Alum */}
                            <fieldset className="nested-fieldset">
                                <legend className="fieldset-legend">Alum Dosing</legend>
                                <div className="inline-grid-2">
                                    <div className="form-group">
                                        <label className="form-label text-gray">Dosage (ppm)</label>
                                        <Field type="number" name="alum_dosage" className="form-input" placeholder="8.2" />
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label text-gray">Strength (%)</label>
                                        <Field type="number" name="alum_strength" className="form-input" placeholder="50" />
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label text-gray">Aerator Flow</label>
                                        <Field type="number" name="alum_aerator_flow" className="form-input" placeholder="15" />
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label text-gray">D Chamber</label>
                                        <Field type="number" name="alum_d_chamber" className="form-input" placeholder="12" />
                                    </div>
                                </div>
                            </fieldset>

                            {/* Post-Lime */}
                            <fieldset className="nested-fieldset">
                                <legend className="fieldset-legend">Post-Lime Dosing</legend>
                                <div className="form-group">
                                    <label className="form-label">SPH1</label>
                                    <div className="inline-grid-2">
                                        <Field type="number" name="post_lime_sph1_dosage" className="form-input" placeholder="Dosage" />
                                        <Field type="number" name="post_lime_sph1_flow" className="form-input" placeholder="Flow" />
                                    </div>
                                </div>
                                <div className="form-group">
                                    <label className="form-label">SPH2</label>
                                    <div className="inline-grid-2">
                                        <Field type="number" name="post_lime_sph2_dosage" className="form-input" placeholder="Dosage" />
                                        <Field type="number" name="post_lime_sph2_flow" className="form-input" placeholder="Flow" />
                                    </div>
                                </div>
                            </fieldset>
                        </div>
                    </section>

                    {/* Section 4: Chlorine Dosing */}
                    <section className="section-card">
                        <h2 className="section-title">Chlorine Dosing <span className="required-mark">*</span></h2>
                        <div className="inline-grid-3">
                            <fieldset className="nested-fieldset">
                                <legend className="fieldset-legend">Pre-Chlorine</legend>
                                <div className="form-group">
                                    <Field
                                        type="number"
                                        name="pre_chlorine_dosage"
                                        className="form-input"
                                        placeholder="Dosage (ppm)"
                                        min="0"
                                        step="0.1"
                                    />
                                    <ErrorDisplay name="pre_chlorine_dosage" />
                                </div>
                                <div className="form-group">
                                    <Field
                                        type="number"
                                        name="pre_chlorine_usage"
                                        className="form-input"
                                        placeholder="Usage (kg/h)"
                                        min="0"
                                        step="0.01"
                                    />
                                    <ErrorDisplay name="pre_chlorine_usage" />
                                </div>
                            </fieldset>
                            <fieldset className="nested-fieldset">
                                <legend className="fieldset-legend">Post-Chlorine</legend>
                                <div className="form-group">
                                    <Field
                                        type="number"
                                        name="post_chlorine_dosage"
                                        className="form-input"
                                        placeholder="Dosage (ppm)"
                                        min="0"
                                        step="0.1"
                                    />
                                    <ErrorDisplay name="post_chlorine_dosage" />
                                </div>
                                <div className="form-group">
                                    <Field
                                        type="number"
                                        name="post_chlorine_usage"
                                        className="form-input"
                                        placeholder="Usage (kg/h)"
                                        min="0"
                                        step="0.01"
                                    />
                                    <ErrorDisplay name="post_chlorine_usage" />
                                </div>
                            </fieldset>
                            <div className="nested-fieldset" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                                <label className="form-label">Tank Level (%)</label>
                                <Field
                                    type="number"
                                    name="chlorine_tank_level"
                                    className="form-input"
                                    placeholder="e.g., 65"
                                    min="0"
                                    max="100"
                                    step="0.1"
                                />
                                <ErrorDisplay name="chlorine_tank_level" />
                            </div>
                        </div>
                    </section>

                    {/* Section 5: Settled Water */}
                    <section className="section-card">
                        <h2 className="section-title">Settled Water Quality</h2>
                        <div className="settled-grid">
                            {[
                                { label: 'Pul:Tube', turbField: 'settled_pul_tube_turb', phField: 'settled_pul_tube_ph' },
                                { label: 'Pulsator', turbField: 'settled_pulsator_turb', phField: 'settled_pulsator_ph' },
                                { label: 'CF1', turbField: 'settled_cf1_turb', phField: 'settled_cf1_ph' },
                                { label: 'CF2', turbField: 'settled_cf2_turb', phField: 'settled_cf2_ph' },
                                { label: 'PT1', turbField: 'settled_pt1_turb', phField: 'settled_pt1_ph' },
                                { label: 'PT2', turbField: 'settled_pt2_turb', phField: 'settled_pt2_ph' },
                            ].map((item) => (
                                <fieldset key={item.label} className="mini-fieldset">
                                    <legend className="mini-legend">{item.label}</legend>
                                    <Field type="number" name={item.turbField} className="mini-input" placeholder="Turb" />
                                    <Field type="number" name={item.phField} className="mini-input" placeholder="pH" />
                                </fieldset>
                            ))}
                        </div>
                    </section>

                    {/* Section 6: Filtered & Final */}
                    <div className="grid-layout-2">
                        <section className="section-card">
                            <h2 className="section-title">Filtered Water</h2>
                            <div className="inline-grid-2">
                                <div className="form-group">
                                    <label className="form-label text-gray">Turbidity</label>
                                    <Field type="number" name="filtered_turbidity" className="form-input" placeholder="0.5" />
                                </div>
                                <div className="form-group">
                                    <label className="form-label text-gray">pH</label>
                                    <Field type="number" name="filtered_ph" className="form-input" placeholder="7.1" />
                                </div>
                            </div>
                        </section>

                        <section className="section-card">
                            <h2 className="section-title">Final Water Quality <span className="required-mark">*</span></h2>
                            <div className="inline-grid-3">
                                <div className="form-group">
                                    <label className="form-label text-gray">Turbidity</label>
                                    <Field
                                        type="number"
                                        name="final_turbidity"
                                        className="form-input"
                                        placeholder="0.3"
                                        min="0"
                                        step="0.01"
                                    />
                                    <ErrorDisplay name="final_turbidity" />
                                </div>
                                <div className="form-group">
                                    <label className="form-label text-gray">pH</label>
                                    <Field
                                        type="number"
                                        name="final_ph"
                                        className="form-input"
                                        placeholder="7.0"
                                        min="0"
                                        max="14"
                                        step="0.1"
                                    />
                                    <ErrorDisplay name="final_ph" />
                                </div>
                                <div className="form-group">
                                    <label className="form-label text-gray">Residual Chlorine <span className="required-mark">*</span></label>
                                    <div className="input-wrapper">
                                        <Field
                                            type="number"
                                            name="residual_chlorine"
                                            className="form-input has-unit"
                                            placeholder="0.5-0.8"
                                            min="0.2"
                                            max="1.0"
                                            step="0.01"
                                        />
                                        <span className="unit-badge">mg/L</span>
                                    </div>
                                    <ErrorDisplay name="residual_chlorine" />
                                    <small className="field-hint">Range: 0.2 - 1.0 mg/L</small>
                                </div>
                            </div>
                        </section>
                    </div>

                    {/* Section 7: Info */}
                    <section className="section-card">
                        <h2 className="section-title">Supporting Info</h2>
                        <div className="inline-grid-2">
                            <div className="form-group">
                                <label className="form-label">Operator Name</label>
                                <Field
                                    as="select"
                                    name="operator_name"
                                    className="form-select"
                                >
                                    <option value="">Select Operator</option>
                                    <option value="A. R. Perera">A. R. Perera</option>
                                    <option value="B. C. Silva">B. C. Silva</option>
                                    <option value="D. E. Fernando">D. E. Fernando</option>
                                </Field>
                            </div>
                            <div className="form-group">
                                <label className="form-label">Notes</label>
                                <Field
                                    as="textarea"
                                    name="notes"
                                    className="form-textarea"
                                    rows="3"
                                    placeholder="Any remarks..."
                                />
                                <ErrorDisplay name="notes" />
                            </div>
                        </div>
                        <div className="form-group">
                            <label className="checkbox-label">
                                <Field
                                    type="checkbox"
                                    name="confirm_accuracy"
                                    className="checkbox-input"
                                />
                                <span>I confirm all data entered is accurate.</span>
                            </label>
                            <ErrorDisplay name="confirm_accuracy" />
                        </div>
                    </section>

                    </Form>
                </div>

                {/* --- Sticky Footer --- */}
             

            </div>
            )}
        </Formik>
    );
}
